import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    Alert,
    CircularProgress,
    AppBar,
    Toolbar,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControlLabel,
    useMediaQuery,
    useTheme,
    ThemeProvider,
    createTheme
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../shared/firebase';

const SignatureCanvas = lazy(() => import('../SignatureCanvas'));

// Custom theme
const signatureTheme = createTheme({
    palette: {
        primary: { main: '#1976d2' },
        background: { default: '#f5f5f5', paper: '#ffffff' },
    },
    typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: { borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: 8, textTransform: 'none', fontWeight: 500, padding: '10px 24px' },
            },
        },
    },
});

// Section header helper
const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mt: 3, mb: 1 }}>
        {children}
    </Typography>
);

// Holder info row
const HolderInfoRow = ({ label, value }) => (
    <Grid item xs={6} sm={4}>
        <Typography variant="caption" color="textSecondary">{label}</Typography>
        <Typography variant="body2">{value || '-'}</Typography>
    </Grid>
);

const CofidisSignaturePage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [termsDocument, setTermsDocument] = useState(null);
    const [signaturesComplete, setSignaturesComplete] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const [caseData, setCaseData] = useState(null);
    const [accepted, setAccepted] = useState(false);

    const [signaturePrimer, setSignaturePrimer] = useState('');
    const [signatureSegundo, setSignatureSegundo] = useState('');

    const urlParams = new URLSearchParams(window.location.search);
    const caseId = urlParams.get('caseId');
    const token = urlParams.get('token');

    useEffect(() => {
        if (!caseId || !token) {
            setError('Enlace inválido o incompleto. Por favor, solicite un nuevo enlace.');
            setLoading(false);
            return;
        }

        const fetchCaseData = async () => {
            try {
                // Use Cloud Function to bypass Firestore security rules
                const getSignatureData = httpsCallable(functions, 'getCofidisSignatureData');
                const result = await getSignatureData({ caseId, token });
                setCaseData(result.data);
            } catch (err) {
                console.error('Error fetching case data:', err);
                if (err.code === 'functions/permission-denied') {
                    setError('El enlace ha expirado o no es válido.');
                } else if (err.code === 'functions/not-found') {
                    setError('No se pudo encontrar el caso.');
                } else {
                    setError('Error al cargar los datos. Por favor, inténtelo de nuevo.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchCaseData();
    }, [caseId, token]);

    const handleSubmit = async () => {
        if (!signaturePrimer && !signatureSegundo) {
            setError('Debe proporcionar al menos una firma.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const submitSignature = httpsCallable(functions, 'submitCofidisSignature');
            const payload = {
                caseId,
                token,
                signatureDataPrimer: signaturePrimer || null,
                signatureDataSegundo: signatureSegundo || null
            };
            const result = await submitSignature(payload);
            if (result.data.success) {
                setTermsDocument(result.data.termsDocument || null);
                setSignaturesComplete(result.data.signaturesComplete);
                setSuccess(true);
            } else {
                setError('No se pudo completar la operación.');
            }
        } catch (err) {
            console.error('Error submitting signature:', err);
            setError(err.message || 'Error al guardar las firmas. Verifique que el enlace no haya expirado.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadTerms = async () => {
        setDownloading(true);
        setDownloadError(null);
        try {
            const { fillTCPDF } = await import('../../utils/pdfUtils');
            await fillTCPDF(termsDocument, { requireSignatures: true });
        } catch (err) {
            setDownloadError('No se ha podido descargar la copia. Puede intentarlo de nuevo o solicitarla en info@easydebt.es.');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (success) {
        return (
            <ThemeProvider theme={signatureTheme}>
                <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', pt: 5 }}>
                    <Container maxWidth="sm">
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="h5" color="primary" gutterBottom>¡Gracias!</Typography>
                            <Alert severity="success" sx={{ mb: 3, justifyContent: 'center', '& .MuiAlert-message': { textAlign: 'center' } }}>
                                Las firmas se han guardado correctamente, tu analista asignado continuará la operación con las entidades financieras.
                            </Alert>
                            {signaturesComplete === false && (
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Falta la firma del otro titular para disponer de la copia completa. Puede firmar desde el mismo enlace.
                                </Typography>
                            )}
                            {downloadError && <Alert severity="warning" sx={{ mb: 2 }}>{downloadError}</Alert>}
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                Puede cerrar esta ventana.
                            </Typography>
                            {termsDocument && (
                                <Button variant="contained" size="small" onClick={handleDownloadTerms} disabled={downloading} sx={{ mb: 1, px: 2, py: 0.75 }}>
                                    {downloading ? 'Preparando descarga...' : 'Descargar términos y condiciones firmados'}
                                </Button>
                            )}
                            <Typography variant="caption" component="p" color="text.secondary">
                                También puede solicitar una copia de los términos y condiciones firmados en{' '}
                                <a href="mailto:info@easydebt.es">info@easydebt.es</a>.
                            </Typography>
                        </Paper>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }

    const hasSecondHolder = caseData && caseData.segundoTitular && caseData.segundoTitular.nombre;
    const creditos = caseData?.creditos || [];
    const primer = caseData?.primerTitular;
    const segundo = caseData?.segundoTitular;

    return (
        <ThemeProvider theme={signatureTheme}>
            <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
                <AppBar position="static" elevation={0}>
                    <Toolbar>
                        <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center' }}>
                            EasyDebt | Autorización
                        </Typography>
                    </Toolbar>
                </AppBar>

                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Paper elevation={2} sx={{ p: isMobile ? 2 : 4 }}>
                        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
                            Aceptación de envío Bancario
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        {!error && caseData && !caseData.hidden && (
                            <>
                                {/* PRIMER TITULAR */}
                                <SectionTitle>DATOS DEL PRIMER TITULAR</SectionTitle>
                                <Divider sx={{ mb: 1 }} />
                                {primer && (
                                    <Grid container spacing={1} sx={{ mb: 2 }}>
                                        <HolderInfoRow label="Nombre" value={primer.nombre} />
                                        <HolderInfoRow label="Apellidos" value={primer.apellidos} />
                                        <HolderInfoRow label="DNI" value={primer.dni} />
                                    </Grid>
                                )}

                                {/* SEGUNDO TITULAR */}
                                {hasSecondHolder && (
                                    <>
                                        <SectionTitle>DATOS DEL SEGUNDO TITULAR</SectionTitle>
                                        <Divider sx={{ mb: 1 }} />
                                        <Grid container spacing={1} sx={{ mb: 2 }}>
                                            <HolderInfoRow label="Nombre" value={segundo.nombre} />
                                            <HolderInfoRow label="Apellidos" value={segundo.apellidos} />
                                            <HolderInfoRow label="DNI" value={segundo.dni} />
                                        </Grid>
                                    </>
                                )}

                                {/* INFORMACIÓN DE CRÉDITOS */}
                                {creditos.length > 0 && (
                                    <>
                                        <SectionTitle>INFORMACIÓN DE CRÉDITOS</SectionTitle>
                                        <Divider sx={{ mb: 1 }} />
                                        <TableContainer sx={{ mb: 3 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Banco / Financiera</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Tipo Crédito</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Capital Inicial</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Capital pendiente</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Cuota Mensual</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {creditos.map((credit, index) => (
                                                        <TableRow key={credit.id || index}>
                                                            <TableCell sx={{ fontSize: '0.75rem' }}>{credit.banco || '-'}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.75rem' }}>{credit.tipoCredito || '-'}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.75rem' }}>
                                                                {credit.capitalInicial
                                                                    ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(parseFloat(credit.capitalInicial))
                                                                    : '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.75rem', color: '#d32f2f', fontWeight: 'bold' }}>
                                                                {credit.capitalPendiente
                                                                    ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(parseFloat(credit.capitalPendiente))
                                                                    : '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.75rem' }}>
                                                                {credit.cuotaMensual
                                                                    ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(parseFloat(credit.cuotaMensual))
                                                                    : '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                                {/* ACCEPTANCE CHECKBOX */}
                                <Box sx={{ my: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={accepted}
                                                onChange={(e) => setAccepted(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                Acepto el envío de mi información a las entidades financieras con las cuales EasyDebt colabora con el objetivo de
                                                realizar estudios de reunificación y/o refinanciación de mis créditos, así como los{' '}
                                                <a
                                                    href="https://www.easydebt.es/terminos-y-condiciones"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    términos y condiciones del servicio
                                                </a>.
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </>
                        )}

                        {/* SIGNATURE CANVASES */}
                        {!error && (
                            <Grid container spacing={4} sx={{ mt: 2 }}>
                                <Grid item xs={12} md={hasSecondHolder ? 6 : 12}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Firma Titular {primer && !caseData?.hidden ? `(${primer.nombre || ''})` : ''}
                                        </Typography>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SignatureCanvas
                                                onChange={(sig) => setSignaturePrimer(sig)}
                                                width={isMobile ? 300 : 350}
                                                height={200}
                                                placeholder="Firma Primer Titular"
                                            />
                                        </Suspense>
                                    </Box>
                                </Grid>

                                {(hasSecondHolder || caseData?.hidden) && (
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <Typography variant="h6" gutterBottom>
                                                Firma Segundo Titular {segundo && !caseData?.hidden ? `(${segundo.nombre || ''})` : '(Si aplica)'}
                                            </Typography>
                                            <Suspense fallback={<CircularProgress />}>
                                                <SignatureCanvas
                                                    onChange={(sig) => setSignatureSegundo(sig)}
                                                    width={isMobile ? 300 : 350}
                                                    height={200}
                                                    placeholder="Firma Segundo Titular"
                                                />
                                            </Suspense>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        )}

                        {!error && (
                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={handleSubmit}
                                    disabled={submitting || (!signaturePrimer && !signatureSegundo) || (caseData && !caseData.hidden && !accepted)}
                                >
                                    {submitting ? 'Guardando...' : 'Enviar Firmas'}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default CofidisSignaturePage;
